import { OPEN_STATUS_IDS } from '@btime/constants'
import { is, path, prop } from 'ramda'
import { navigate } from '@reach/router'
import { useIsOwner, useLoggedUser, useSessionPermission } from '@btime/react-session'

import { useNavigationWithModalCloser } from '../../contexts/NavigationContext'
import {
  useOpenCloneServiceOrderModal,
  useOpenCreateTemplateRecordModal,
  useOpenEditServiceOrderModal,
  useOpenEditServiceOrdersModal,
  useOpenEditServiceOrdersStatusModal,
  useOpenEditServiceOrdersUserModal,
  useOpenExecuteServiceOrderModal,
  useOpenExportSelectedServiceOrdersModal,
  useOpenMailPublicExecutionModal,
  useOpenRemoveModal,
  useOpenServiceOrderModal,
  useOpenServiceOrderResumeModal
} from '../../routes/modals/modalsActions'
import { useServiceOrderPermissions } from '../../permissions/serviceOrders/serviceOrderPermissions'
import { useTableActions } from '../../../components/tables/MultiSelectTableContext'
import useCompleteServiceOrder from '../../../modules/serviceOrders/show/_requests/complete'

export const useServiceOrderDetailsAction = item => {
  const openDetailsModal = useOpenServiceOrderModal()
  return { click: () => openDetailsModal(item) }
}

export const useServiceOrderLogsAction = item => {
  const isOwner = useIsOwner()
  const navigate = useNavigationWithModalCloser()

  return {
    click: () => navigate(`/logs?entityServiceOrderIds=${item.id}`),
    link: `/logs?entityServiceOrderIds=${item?.id}`,
    hasPermission: isOwner
  }
}

export const useServiceOrdersLogsAction = ids => {
  const isOwner = useIsOwner()
  const navigate = useNavigationWithModalCloser()

  return {
    click: () => navigate(`/logs?entityServiceOrderIds=${ids?.join(',')}`),
    link: `/logs?entityServiceOrderIds=${ids?.join(',')}`,
    hasPermission: isOwner
  }
}

export const useEditServiceOrderAction = item => {
  const openEditModal = useOpenEditServiceOrderModal()
  const { canEditFinishedServiceOrders, canEdit } = useServiceOrderPermissions()
  const finishedStatuses = ['refused', 'unproductive', 'completed', 'pending', 'canceled', 'approved', 'disapproved']
  const isFinished = finishedStatuses.includes(item?.status?.name)

  return {
    click: () => openEditModal({ id: item.id }),
    hasPermission: isFinished ? canEdit && canEditFinishedServiceOrders : canEdit
  }
}

export const useEditServiceOrdersAction = (ids, statuses = []) => {
  const openEditModal = useOpenEditServiceOrdersModal()
  const { canEditFinishedServiceOrders, canEdit } = useServiceOrderPermissions()
  const finishedStatuses = ['refused', 'unproductive', 'completed', 'pending', 'canceled', 'approved', 'disapproved']
  const hasFinished = statuses?.some(item => finishedStatuses.includes(item))
  const isAbleToEdit = canEdit && ids?.length <= 150
  return {
    click: () => openEditModal({ ids }),
    hasPermission: hasFinished ? isAbleToEdit && canEditFinishedServiceOrders : isAbleToEdit
  }
}

export const useCreateServiceOrderChildAction = item => {
  const openEditModal = useOpenEditServiceOrderModal()
  const canCreateSO = useSessionPermission('createServiceOrder')

  return {
    click: () => openEditModal({ initialState: { parentId: item.id, parentDependent: false } }),
    hasPermission: canCreateSO
  }
}

export const useMailPublicServiceOrderExecutionLinkAction = item => {
  const maiPubliclExecution = useOpenMailPublicExecutionModal()
  const hasPublicExecutionPermission = useSessionPermission('executePublicServiceOrder')
  const canSeePublicExecutionLink = OPEN_STATUS_IDS.includes(item?.status?.id)

  return {
    click: () => maiPubliclExecution([item?.id]),
    hasPermission: hasPublicExecutionPermission && canSeePublicExecutionLink
  }
}

export const useMailPublicServiceOrdersExecutionLinkAction = ids => {
  const maiPubliclExecution = useOpenMailPublicExecutionModal()
  const hasPublicExecutionPermission = useSessionPermission('executePublicServiceOrder')

  return {
    click: () => maiPubliclExecution(ids),
    hasPermission: hasPublicExecutionPermission
  }
}

export const useServiceOrderExecutionAction = (item, usingModal) => {
  const openExecutionModal = useOpenExecuteServiceOrderModal()
  const { canExecuteFromWeb } = useServiceOrderPermissions()
  const executeStatuses = ['received', 'open', 'execution', 'pause', 'pending']
  const userId = path(['user', 'id'], item) || prop('userId', item)
  const loggedUser = useLoggedUser()

  return {
    click: () => {
      if (usingModal) {
        openExecutionModal(item)
      } else {
        navigate(`/service-orders/${item.id}/execution`)
      }
    },
    link: `/service-orders/${prop('id', item)}/execution`,
    hasPermission: executeStatuses.includes(item?.status?.name) && loggedUser.id === userId && canExecuteFromWeb
  }
}

export const useServiceOrderResumeAction = (item, usingModal) => {
  const openResumeModal = useOpenServiceOrderResumeModal()
  const navigate = useNavigationWithModalCloser()
  const { canSeeResume } = useServiceOrderPermissions(item)

  return {
    click: () => {
      if (usingModal) {
        openResumeModal(item)
      } else {
        navigate(`/service-orders/${item?.id}/resume`)
      }
    },
    link: `/service-orders/${prop('id', item)}/resume`,
    hasPermission: canSeeResume
  }
}

export const useEditServiceOrderUserAction = item => {
  const openEditServiceOrderUserModal = useOpenEditServiceOrdersUserModal()
  const { canEditUser } = useServiceOrderPermissions(item)

  return {
    click: () => openEditServiceOrderUserModal(item),
    hasPermission: canEditUser
  }
}

export const useCloneServiceOrderAction = item => {
  const canEdit = useSessionPermission('editServiceOrder')
  const openCloneModal = useOpenCloneServiceOrderModal()

  return { click: () => openCloneModal(item), hasPermission: canEdit }
}

export const useEditServiceOrdersStatusAction = (ids, mainStatusName) => {
  const hasPermission = useSessionPermission('editServiceOrdersStatus')
  const onOpenEditStatusModal = useOpenEditServiceOrdersStatusModal()

  const isAbleToChangeStatus = hasPermission && ids?.length <= 450

  return {
    click: statusName =>
      onOpenEditStatusModal({ ids, statusName: is(String, statusName) ? statusName : mainStatusName }),
    hasPermission: isAbleToChangeStatus
  }
}

export const useExportSelectedServiceOrdersAction = ids => {
  const hasPermission = useSessionPermission('exportServiceOrder')
  const onOpenExportSelectedServiceOrdersModal = useOpenExportSelectedServiceOrdersModal()

  return {
    click: () => onOpenExportSelectedServiceOrdersModal({ ids }),
    hasPermission
  }
}

export const useServiceOrderDocGeneratorAction = item => {
  const openCreateTemplateRecordModal = useOpenCreateTemplateRecordModal()
  const canCreateTemplateRecord = useSessionPermission('createTemplateRecord')
  return {
    click: () =>
      openCreateTemplateRecordModal({
        entity: 'service_order',
        recordId: item.id,
        checklistIds: item?.checklistId || item?.checklist?.id
      }),
    hasPermission: canCreateTemplateRecord
  }
}

export const useRemoveServiceOrderAction = item => {
  const canRemove = useSessionPermission('deleteServiceOrder')
  const openRemoveModal = useOpenRemoveModal()
  const { clearSelection } = useTableActions()
  return { click: () => openRemoveModal({ ids: [item?.id], onSuccess: clearSelection }), hasPermission: canRemove }
}

export const useRemoveServiceOrdersAction = ids => {
  const canRemove = useSessionPermission('deleteServiceOrder')
  const openRemoveModal = useOpenRemoveModal()
  const { clearSelection } = useTableActions()
  return { click: () => openRemoveModal({ ids, onSuccess: clearSelection }), hasPermission: canRemove }
}

export const useCompleteServiceOrdersAction = item => {
  const [completeRequest] = useCompleteServiceOrder()
  return {
    click: () => {
      completeRequest(item)
    }
  }
}
